import { LeaveOrganization } from '@/assets/icons'
import { Modal, ModalContent, ModalTitle } from '@/components/modal/Modal'
import { Button } from '@/components/button/Button'
import { MembersRoster, type MembersRosterProps } from './MembersRoster'

// Re-exported for existing consumers (e.g. SidebarAccountMenu) that import these from here.
export type { MemberRosterMember, BillingStats } from './MembersRoster'

export interface MemberRosterModalProps extends MembersRosterProps {
  open: boolean
  onClose: () => void
  /** Opens the self-removal confirmation flow. Omit for the organization payer. */
  onLeaveOrganization?: () => void
}

/**
 * Standalone member roster dialog opened from the sidebar account menu. Thin wrapper that supplies
 * the modal chrome (overlay, close button, Leave Organization footer) around the shared
 * presentational <MembersRoster/>; the settings-revamp Members panels render <MembersRoster/>
 * directly inside the settings modal instead.
 */
export function MemberRosterModal({ open, onClose, onLeaveOrganization, ...rosterProps }: MemberRosterModalProps) {
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()}>
      <ModalContent
        width="page"
        height="page"
        // On mobile, grow to nearly the full viewport (1rem gutter) instead of the 85dvh clamp so the
        // member list has room to show several rows. Desktop keeps the centered, clamped `page` dialog.
        className="max-sm:h-[calc(100dvh-2rem)] max-sm:max-h-none"
        aria-describedby={undefined}
        showCloseButton
        footer={
          onLeaveOrganization && (
            <div className="flex justify-end">
              <Button variant="plain" color="destructive" leadingIcon={<LeaveOrganization className="size-4" aria-hidden />} onClick={onLeaveOrganization}>
                Leave Organization
              </Button>
            </div>
          )
        }
      >
        {/* The visible org name is a plain heading inside MembersRoster; this hidden Dialog.Title
            gives the dialog its accessible name (Radix requires a Title on DialogContent). */}
        <ModalTitle className="sr-only">{rosterProps.orgName}</ModalTitle>
        <MembersRoster {...rosterProps} />
      </ModalContent>
    </Modal>
  )
}
